import React, { FunctionComponent, ReactElement, useMemo } from 'react'
import Grid from '@sport1/react-elements/Grid'
import Touchable from '@sport1/react-elements/Touchable'
import MoreIcon from '../../BaseComponents/Icons/MoreIcon'
import { TestID } from '../../utils/PropTypesDefinitions'
import Bet from '../Bet'
import LinkProps from '../../Link/LinkProps'
import HighlightRow from '../HighlightRow'
import OptionalField from './OptionalField'
import { BasicMatchProps } from './MatchTypes'
import MatchCompetitor from './HelperClass/MatchCompetitor'

type MatchTemplateProps = Pick<
    BasicMatchProps,
    | 'additionalField1'
    | 'additionalField2'
    | 'awayCompetitor'
    | 'homeCompetitor'
    | 'onPressMatch'
    | 'matchHref'
    | 'onPressMore'
    | 'onPressVideoButton'
    | 'videoButtonText'
    | 'videoButtonHref'
    | 'minify'
    | 'isSingleMatch'
    | 'betProps'
    | 'paddingTop'
    | 'borderRadius'
    | 'forceMobileView'
> & {
    center: ReactElement
    prefix?: ReactElement
    centerComponentWidth?: 70 | 58
} & LinkProps &
    TestID

const MatchTemplate: FunctionComponent<MatchTemplateProps> = ({
    additionalField1,
    additionalField2,
    awayCompetitor,
    center,
    homeCompetitor,
    onPressMatch,
    matchHref,
    onPressMore,
    prefix,
    testID,
    minify,
    isSingleMatch,
    centerComponentWidth = 58,
    betProps,
    paddingTop = ['12px', '12px', '12px', '7px'],
    borderRadius,
    forceMobileView,
    webRel,
    onPressVideoButton,
    videoButtonHref,
    videoButtonText,
}) => {
    const hasOptionalField = additionalField1 || additionalField2
    const leftSpace = useMemo(() => {
        if (!isSingleMatch || prefix) {
            return minify ? 12 : 20
        }
        return 0
    }, [isSingleMatch, minify, prefix])
    return (
        <Grid
            minWidth={0}
            noExpand
            position="relative"
            overflow="hidden"
            borderRadius={borderRadius}
            backgroundColor="pearl"
            testID={testID}
        >
            <Grid
                paddingTop={paddingTop}
                paddingBottom={['12px', '12px', '12px', '7px']}
                paddingX={forceMobileView ? 11 : [11, 11, 11, 3]}
                minWidth={0}
                horizontal
                noExpand
            >
                <Grid minWidth={0} marginRight={!betProps && !forceMobileView ? [0, 0, 0, 376] : 0}>
                    <Touchable onPress={onPressMatch} href={matchHref} webRel={webRel}>
                        <Grid
                            horizontal
                            noExpand
                            marginBottom={hasOptionalField ? 2 : 0}
                            centerVertical
                            minWidth={0}
                            minHeight={[0, 0, 0, 30]}
                        >
                            {prefix ? (
                                <Grid noExpand position="absolute">
                                    {prefix}
                                </Grid>
                            ) : null}
                            <MatchCompetitor
                                type="home"
                                textPaddingLeft={leftSpace}
                                {...homeCompetitor}
                            />
                            <Grid noExpand width={centerComponentWidth} centerHorizontal>
                                {center}
                            </Grid>
                            <MatchCompetitor
                                type="away"
                                textPaddingRight={
                                    onPressMore ? [24, 24, 24, forceMobileView ? 24 : 0] : 0
                                }
                                {...awayCompetitor}
                            />
                        </Grid>
                        <OptionalField data={additionalField1} />
                        <OptionalField data={additionalField2} />
                    </Touchable>
                    {onPressVideoButton || videoButtonHref ? (
                        <HighlightRow
                            text={videoButtonText}
                            onPress={onPressVideoButton}
                            href={videoButtonHref}
                            marginTop={3}
                            marginBottom={[0, 0, 0, 2]}
                        />
                    ) : null}
                </Grid>
                {betProps && !forceMobileView ? (
                    <Bet
                        width={342}
                        display={['none', 'none', 'none', 'flex']}
                        marginRight={34}
                        {...betProps}
                    />
                ) : null}
            </Grid>
            {onPressMore ? (
                <Grid
                    noExpand
                    position="absolute"
                    right={0}
                    top={[0, 0, 0, 5]}
                    bottom={0}
                    paddingTop={
                        forceMobileView && Array.isArray(paddingTop) ? paddingTop[0] : paddingTop
                    }
                >
                    <Touchable onPress={onPressMore}>
                        <Grid centerHorizontal noExpand marginRight={12}>
                            <MoreIcon />
                        </Grid>
                    </Touchable>
                </Grid>
            ) : null}
            {betProps ? (
                <Bet
                    display={forceMobileView ? 'flex' : ['flex', 'flex', 'flex', 'none']}
                    {...betProps}
                />
            ) : null}
        </Grid>
    )
}

export default MatchTemplate
